import { FORTRESS_MIN_ANGLE, FORTRESS_TERRAIN_HEIGHT, type FortressPoint } from './core';

export const FORTRESS_MOVE_REPEAT_MILLISECONDS = 80;

export function fortressArenaProjection(worldWidth: number, canvasWidth: number, canvasHeight: number): { scale: number; offsetY: number } {
  const scale = canvasWidth / worldWidth;
  return { scale, offsetY: canvasHeight - FORTRESS_TERRAIN_HEIGHT * scale };
}

export type FortressDirection = -1 | 1;
export type FortressControl = 'move-left' | 'move-right' | 'aim-up' | 'aim-down'
  | 'coconut' | 'precision-missile' | 'charge';

const KEY_CONTROLS: Readonly<Record<string, FortressControl>> = {
  ArrowLeft: 'move-left', KeyA: 'move-left',
  ArrowRight: 'move-right', KeyD: 'move-right',
  ArrowUp: 'aim-up', KeyW: 'aim-up',
  ArrowDown: 'aim-down', KeyS: 'aim-down',
  Digit1: 'coconut', Digit2: 'precision-missile', Space: 'charge',
};

export function fortressKeyboardControl(event: { readonly code: string; readonly key: string }): FortressControl | null {
  // Physical keys keep WASD working while a Korean keyboard layout is active.
  return KEY_CONTROLS[event.code] ?? KEY_CONTROLS[event.key]
    ?? KEY_CONTROLS[`Key${event.key.toUpperCase()}`] ?? null;
}

export function fortressAimElevation(angle: number): number {
  return Math.min(angle, 180 - angle);
}

export function fortressAngleFacing(angle: number, facing: FortressDirection): number {
  const elevation = fortressAimElevation(angle);
  return facing === 1 ? elevation : 180 - elevation;
}

export function fortressAdjustAngle(angle: number, direction: FortressDirection, facing: FortressDirection): number {
  const elevation = Math.max(FORTRESS_MIN_ANGLE, Math.min(90, fortressAimElevation(angle) + direction));
  // Keep the last facing at 90 degrees so lowering never flips the cannon.
  return facing === 1 ? elevation : 180 - elevation;
}

export function fortressMovementPosition(from: FortressPoint, to: FortressPoint, elapsedMilliseconds: number): FortressPoint {
  const progress = Math.max(0, Math.min(1, elapsedMilliseconds / FORTRESS_MOVE_REPEAT_MILLISECONDS));
  return { x: from.x + (to.x - from.x) * progress, y: from.y + (to.y - from.y) * progress };
}
